import { sub } from 'date-fns'
import { useEffect, useState } from 'react'

import { useGetPluginPingsStatusQuery } from '../../app/apiSliceCoreLogicApi'
import { Loader } from '../../common/components'
import { useDashboard } from '../../common/hooks'
import { ChartData } from '../../common/types/common'
import {
  GranularityEnum,
  OrganizationLevelEnum,
  type PluginPingsPayload,
} from '../../common/types/monitoring'
import { getColorBasedOnValuePercentage } from '../../common/utils/colorUtils'
import { getDatesInRange } from '../../common/utils/dateTimeUtils'
import ConnectivityGraph from './ConnectivityGraph'

interface Props {
  daysSelected: number
  diffWithDate: string
  onDiffWithDateChange: (date: string) => void
  expectedCompanyTarget?: number | undefined
}

const CompanyDataGraph = ({
  daysSelected,
  diffWithDate,
  onDiffWithDateChange,
  expectedCompanyTarget,
}: Props) => {
  const { company } = useDashboard()
  const expectedTarget = expectedCompanyTarget ?? company.agentInstallationTarget

  const [chartData, setChartData] = useState<null | ChartData>(null)
  const [timePeriod, setTimePeriod] = useState<{
    start_timestamp: string
    end_timestamp: string
  } | null>(null)

  const { data, isFetching } = useGetPluginPingsStatusQuery(
    {
      start_timestamp: timePeriod?.start_timestamp ?? '',
      end_timestamp: timePeriod?.end_timestamp ?? '',
      granularity: GranularityEnum.DAY,
      organization_level: OrganizationLevelEnum.COMPANY,
    },
    { skip: !timePeriod },
  )

  useEffect(() => {
    const today = new Date() // Defaults time to host computer!

    setTimePeriod({
      start_timestamp: sub(today, { days: daysSelected - 1 }).toISOString(),
      end_timestamp: today.toISOString(),
    })
  }, [daysSelected])

  useEffect(() => {
    if (!timePeriod || !data) return

    // Generate the array of dates
    const datesArray = getDatesInRange(
      new Date(timePeriod.start_timestamp),
      new Date(timePeriod.end_timestamp),
    )

    // Map the dates to the API data
    const rawChartData = datesArray.map((date) => {
      const dateStr = date.toISOString().split('T')[0] // Format date as 'YYYY-MM-DD'
      const apiDataForDate = data.find((d: PluginPingsPayload[number]) =>
        d.grouping_timestamp.startsWith(dateStr),
      )

      return {
        label: dateStr,
        count: apiDataForDate ? apiDataForDate.computer_count : 0,
        backgroundColor: getColorBasedOnValuePercentage(
          apiDataForDate ? apiDataForDate.computer_count : 0,
          expectedTarget,
        ),
      }
    })

    const chartData = {
      labels: rawChartData.map((item) => item.label),
      datasets: [
        {
          label: 'Agents connected',
          data: rawChartData.map((item) => item.count),
          backgroundColor: rawChartData.map((item) => item.backgroundColor),
        },
      ],
    }

    setChartData(chartData)
    if (!diffWithDate || !chartData.labels.includes(diffWithDate)) {
      onDiffWithDateChange(chartData.labels[0])
    }
  }, [data, timePeriod, expectedTarget, diffWithDate, onDiffWithDateChange])

  if (!chartData || isFetching) return <Loader />

  return (
    <ConnectivityGraph
      title='All Computers In The Company'
      data={chartData}
      expectedAgentAmount={expectedTarget}
      diffWithDate={diffWithDate}
      onDiffWithDateChange={onDiffWithDateChange}
    />
  )
}

export default CompanyDataGraph
